import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SearchInput } from "../components/search/SearchInput";
import { Brain, ChevronLeft, ChevronRight, Send, History, AlertTriangle } from "lucide-react";
import { AuraBackground } from "../components/AuraBackground";
import { SearchHeader } from "../components/SearchHeader";
import { useRecordsApi } from "../lib/api/records";
import { RagResultCard } from "../components/rag/RagResultCard";
import { RagConfigToggle } from "../components/rag/RagConfigToggle";
import type { DocumentRecord } from "../types/record";
import { appConfig } from "../config/appConfig";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import type { DeltaStatic } from "quill";
import { ChatMessage as ChatMessageComponent } from "../components/chat/ChatMessage";
import { ConversationHistorySidebar } from "../components/chat/ConversationHistorySidebar";
import { flushSync } from "react-dom";
import { useChat } from "../contexts/ChatContext";
import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../contexts/UsageContext";
import { useQueryClient } from "@tanstack/react-query";
import { NoSubscriptionScreen } from "../components/chat/NoSubscriptionScreen";
import { UsageLimitBanner } from "../components/chat/UsageLimitBanner";

// Import chat API
import { chatApi, sendChatMessageStreamFetch } from "../lib/api/chat";
import type {
  ChatMessage as ChatMessageType,
  ChatSource,
  IntentType,
  ChatRequest,
  ChatStreamChunk,
} from "../types/chat";

// Import conversation service for loading message history
import { conversationService, ConversationAccessError } from "../services/conversationService";
import type { Message as ConversationMessage } from "../types/conversation";

import intelligentiaLogo from "../images/intelligentiahumana.jpg";

// Define the RagSource interface for backward compatibility
interface RagSource {
  id: string;
  title?: string;
  score: number;
  content?: string;
  formatted_content?: DeltaStatic;
}

// Define the Message interface for chat
interface Message {
  role: "user" | "assistant";
  content: string;
  sources?: RagSource[];
  timestamp: number;
  conversationId?: string;
  messageIndex?: number;
  intent?: IntentType;
  classification?: {
    intent: IntentType;
    confidence: number;
    reasoning?: string;
  };
  isStreaming?: boolean;
  isHistorical?: boolean; // Flag to indicate if message is from chat history (no typewriter effect)
  isConversationNotice?: boolean; // Flag to indicate this is a conversation continuation notice
}

export function ChatPage() {
  const recordsApi = useRecordsApi(); // Keep for document loading
  const queryClient = useQueryClient();
  const {
    conversationId,
    setConversationId,
    createNewConversation,
    clearConversation,
  } = useChat();
  const { clerkUser } = useAuth();
  const { hasSubscription, usage, updateRemainingFromResponse } = useUsage();
  const { conversationId: urlConversationId } = useParams<{
    conversationId: string;
  }>();
  const navigate = useNavigate();

  // Check if conversation history feature is enabled
  const showConversationHistory = appConfig.chat.enableConversationHistory;
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  // Keep sessionId for backwards compatibility but prefer conversationId
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  const [thinkingTime, setThinkingTime] = useState<number>(0);
  const thinkingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<RagSource | null>(
    null
  );
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const quillRef = useRef<ReactQuill>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState("");

  // Load chat history for a conversation
  const loadChatHistory = async (conversationId: string) => {
    try {
      setIsLoading(true);
      console.log("Loading chat history for conversation:", conversationId);

      if (!clerkUser) {
        throw new Error("User not authenticated");
      }

      // Use the conversation service to get the actual stored messages
      const conversationDetail =
        await conversationService.getConversationDetail(conversationId);

      // Convert ConversationMessage[] to Message[] for the chat UI
      const convertedMessages: Message[] = conversationDetail.messages.map(
        (msg: ConversationMessage, index: number) => ({
          role: msg.sender as "user" | "assistant",
          content: msg.content,
          timestamp: new Date(msg.created_at).getTime(),
          conversationId: conversationId,
          messageIndex: msg.message_index || index, // Use stored message_index or fallback to array index
          sources: [], // Chat UI doesn't show sources from history yet
          isHistorical: true, // Mark historical messages to disable typewriter effect
        })
      );

      // Add conversation continuation notice ONLY if we're loading an existing conversation with actual messages
      // This should NOT appear for new conversations that happen to have a conversation ID but no stored messages
      if (convertedMessages.length > 0) {
        const continuationNotice: Message = {
          role: "assistant", // Use assistant role for styling consistency
          content: `Reprise de la conversation du ${new Date(
            convertedMessages[0]?.timestamp || Date.now()
          ).toLocaleDateString()} • ${convertedMessages.length} message${
            convertedMessages.length !== 1 ? "s" : ""
          } chargé${convertedMessages.length !== 1 ? "s" : ""}`,
          timestamp: Date.now(),
          isHistorical: true,
          isConversationNotice: true, // Special flag for styling
        };
        convertedMessages.push(continuationNotice);
      }

      // Only overwrite messages if there are no current messages OR
      // if the loaded messages are more recent than what we have
      setMessages((currentMessages) => {
        // If we have current messages and the history is empty, keep current messages
        // This prevents overwriting messages that were just added for a new conversation
        if (currentMessages.length > 0 && convertedMessages.length === 0) {
          console.log(
            "Preserving current messages - history is empty for new conversation"
          );
          return currentMessages;
        }

        // If history is empty (no stored messages), don't add anything including continuation notice
        if (convertedMessages.length === 0) {
          console.log("No messages found in conversation history");
          return [];
        }

        // If both have messages, use the history (existing conversation)
        console.log(
          `Loaded ${convertedMessages.length} messages from conversation history`
        );
        return convertedMessages;
      });
    } catch (error: any) {
      console.error("Failed to load conversation history:", error);
      console.log("Error type:", error?.constructor?.name);
      console.log("Error status:", error?.status);

      // Feature: 001-user-role-management (T044) - Handle 401/403 when loading conversation
      if (error instanceof ConversationAccessError && (error.status === 401 || error.status === 403)) {
        console.log("Access denied detected, setting accessDenied to true");
        const errorMessage = error.detail || "You don't have permission to access this conversation";
        setError(`Access Denied: ${errorMessage}`);
        setAccessDenied(true);

        // Don't set messages - let the empty state show the access denied message
        setMessages([]);
      } else {
        setError("Failed to load conversation history");
        // Don't fail completely, just preserve current messages if any
        setMessages((currentMessages) => {
          if (currentMessages.length > 0) {
            console.log("Preserving current messages due to history load error");
            return currentMessages;
          }
          return [];
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Sync conversation ID from URL and load history if available
  useEffect(() => {
    if (urlConversationId) {
      // Set conversation ID from URL
      setConversationId(urlConversationId);

      // Load chat history for this conversation
      loadChatHistory(urlConversationId);
    } else {
      // No conversation ID in URL, clear current conversation
      setConversationId(null);
      setMessages([]);
    }
  }, [urlConversationId, setConversationId]);

  const clearSession = () => {
    // Navigate to /chat to start a new conversation
    navigate("/chat");

    // Clear messages and conversation state
    clearConversation();
    setMessages([]);
    setAccessDenied(false);
    setError(null);

    // Generate new session ID for backwards compatibility
    setSessionId(crypto.randomUUID());

    // Refresh conversation history sidebar
    queryClient.invalidateQueries({ queryKey: ['my-conversations'] });
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Convert ChatSource to RagSource for UI compatibility
  const convertChatSourceToRagSource = (source: ChatSource): RagSource => ({
    id: source.id,
    title: source.title,
    score: source.score || 0,
    content: source.content,
  });

  // Prepare chat history for API request
  const prepareChatHistory = (): ChatMessageType[] => {
    return messages.slice(-10).map((msg) => ({
      role: msg.role as "user" | "assistant",
      content: msg.content,
      timestamp: new Date(msg.timestamp).toISOString(),
    }));
  };

  // Check if conversation has reached message limit
  const checkMessageLimit = (): boolean => {
    // Count ALL messages in the conversation (historical + new), excluding only conversation notices
    const actualMessages = messages.filter((msg) => !msg.isConversationNotice);

    // Check if we've reached 10 total messages (user + assistant)
    return actualMessages.length >= 10;
  };

  // Handle search with new intent-based chat API
  const handleSearch = async (newQuery: string) => {
    if (!newQuery.trim()) return;

    // Check message limit before proceeding
    if (checkMessageLimit()) {
      // Show error and force new conversation
      setError(
        "Cette conversation a atteint la limite de 10 messages. Démarrage d'une nouvelle conversation pour de meilleures réponses IA avec un contexte limité."
      );

      // Wait a moment for user to see the message, then start new conversation
      setTimeout(() => {
        clearSession();
      }, 3000);
      return;
    }

    setError(null);
    setInputValue("");

    // Add user message
    const userMessage: Message = {
      role: "user",
      content: newQuery,
      timestamp: Date.now(),
      conversationId: conversationId || undefined,
      messageIndex: messages.length, // Index for this new message
      isHistorical: false, // New user messages are not historical
    };
    console.log("Adding user message to chat:", userMessage);
    setMessages((prev) => {
      console.log("Current messages before adding user message:", prev.length);
      return [...prev, userMessage];
    });

    // Cancel any ongoing generation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsLoading(true);
    startThinkingTimer();

    try {
      if (appConfig.rag.useStreaming) {
        await handleStreamingChat(newQuery);
      } else {
        await handleSingleCallChat(newQuery);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Chat error:", error);
        setError(
          error instanceof Error
            ? error.message
            : "An error occurred while generating the response"
        );
      }
    } finally {
      setIsLoading(false);
      stopThinkingTimer();

      // Refresh conversation history sidebar after message is sent
      queryClient.invalidateQueries({ queryKey: ['my-conversations'] });
    }
  };

  // Handle single call chat (non-streaming)
  const handleSingleCallChat = async (query: string) => {
    try {
      let currentConversationId = conversationId;

      // If no conversation ID, create a new conversation
      if (!currentConversationId) {
        console.log("Creating new conversation for single call chat");
        currentConversationId = await createNewConversation();
        console.log("Navigating to new conversation:", currentConversationId);
        // Navigate to the new conversation URL
        navigate(`/chat/${currentConversationId}`);
      }

      if (!clerkUser) {
        throw new Error("User not authenticated");
      }

      const chatRequest: ChatRequest = {
        message: query,
        conversation_id: currentConversationId,
        session_id: sessionId, // Keep for backwards compatibility
      };

      const response = await chatApi.sendMessage(chatRequest);

      if (response.usage_remaining !== undefined && response.usage_remaining !== null) {
        updateRemainingFromResponse(response.usage_remaining);
      }

      // Add assistant message
      const assistantMessage: Message = {
        role: "assistant",
        content: response.response,
        sources: [], // Chat endpoint doesn't return sources
        timestamp: Date.now(),
        isHistorical: false, // New assistant messages are not historical
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      handleChatError(error);
    }
  };

  // Handle streaming chat with chat API
  const handleStreamingChat = async (query: string) => {
    try {
      let responseText = "";
      let currentConversationId = conversationId;

      // If no conversation ID, create a new conversation
      if (!currentConversationId) {
        console.log("Creating new conversation for streaming chat");
        currentConversationId = await createNewConversation();
        console.log("Navigating to new conversation:", currentConversationId);
        // Navigate to the new conversation URL
        navigate(`/chat/${currentConversationId}`);
      }

      if (!clerkUser) {
        throw new Error("User not authenticated");
      }

      const chatRequest: ChatRequest = {
        message: query,
        conversation_id: currentConversationId,
        session_id: sessionId, // Keep for backwards compatibility
        use_streaming: true,
      };

      // Add initial assistant message with streaming flag
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "",
          timestamp: Date.now(),
          conversationId: currentConversationId,
          messageIndex: prev.length, // Index for this new assistant message
          isStreaming: true,
          isHistorical: false, // New streaming messages are not historical
        },
      ]);

      // Stream response using the chat API
      for await (const chunk of sendChatMessageStreamFetch(chatRequest)) {
        console.log("Received chunk:", chunk);

        if (!chunk.done) {
          // Add chunk content if present (including empty strings for formatting)
          if (chunk.chunk !== undefined) {
            responseText += chunk.chunk;
          }

          // Batch updates for better performance - update UI less frequently
          // This reduces the number of re-renders while maintaining responsiveness
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === "assistant") {
              lastMessage.content = responseText;
              lastMessage.isStreaming = true;
            }
            return newMessages;
          });
        }

        if (chunk.done) {
          // Check if this is an access denied error
          if (chunk.chunk && chunk.chunk.includes("Access Denied:")) {
            setAccessDenied(true);
            setError(chunk.chunk);
            // Update the assistant message with the error
            setMessages((prev) => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.role === "assistant") {
                lastMessage.content = chunk.chunk;
                lastMessage.isStreaming = false;
              }
              return newMessages;
            });
            break;
          }

          // Final chunk with metadata
          if (chunk.full_response) {
            responseText = chunk.full_response;
          }

          if (chunk.usage_remaining !== undefined && chunk.usage_remaining !== null) {
            updateRemainingFromResponse(chunk.usage_remaining);
          }

          // Final update with complete data and mark streaming as complete
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === "assistant") {
              lastMessage.content = responseText;
              (lastMessage.sources = []), // Chat endpoint doesn't return sources
                (lastMessage.isStreaming = false);
            }
            return newMessages;
          });
          break;
        }
      }
    } catch (error) {
      handleChatError(error);
    }
  };

  // Handle chat errors
  // Feature: 001-user-role-management (T043-T044) - Handle 403 authorization errors
  const handleChatError = (error: any) => {
    console.error("Chat error:", error);

    // Check for 403 Forbidden (authorization error)
    if (error?.response?.status === 403) {
      const errorMessage =
        error?.response?.data?.detail ||
        "You don't have permission to access this conversation";

      setAccessDenied(true);

      // Add specific 403 error message to chat
      const errorMessageObj: Message = {
        role: "assistant",
        content: `Access Denied: ${errorMessage}. This conversation may belong to another user.`,
        timestamp: Date.now(),
        isHistorical: false,
      };
      setMessages((prev) => [...prev, errorMessageObj]);
      setError(errorMessage);
      return;
    }

    // Handle other errors
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      "An error occurred while processing your request";

    // Add error message to chat
    const errorMessageObj: Message = {
      role: "assistant",
      content: `Sorry, I encountered an error: ${errorMessage}`,
      timestamp: Date.now(),
      isHistorical: false, // Error messages are new messages, not historical
    };
    setMessages((prev) => [...prev, errorMessageObj]);
  };

  const startThinkingTimer = () => {
    setThinkingTime(0);
    thinkingTimerRef.current = setInterval(() => {
      setThinkingTime((prev) => prev + 0.1);
    }, 100);
  };

  const stopThinkingTimer = () => {
    if (thinkingTimerRef.current) {
      clearInterval(thinkingTimerRef.current);
      thinkingTimerRef.current = null;
    }
  };

  const formatThinkingTime = (
    time: number,
    showMilliseconds: boolean = false
  ): string => {
    if (time < 1) {
      return showMilliseconds ? `${(time * 1000).toFixed(0)}ms` : "<1";
    } else if (time < 60) {
      return time.toFixed(1);
    } else {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}m ${seconds}s`;
    }
  };

  // Document handling functions remain the same
  const handleDocumentClick = async (document: RagSource) => {
    setSelectedDocument({ ...document, content: "Loading content..." });
    setIsDocumentLoading(true);

    try {
      const response = await recordsApi.getDocumentContent(document.id);
      setSelectedDocument((prev) =>
        prev
          ? {
              ...prev,
              content: response.content,
              formatted_content: response.formatted_content,
            }
          : null
      );
    } catch (error) {
      console.error("Error loading document:", error);
      setSelectedDocument((prev) =>
        prev
          ? {
              ...prev,
              content: "Error loading content. Please try again.",
            }
          : null
      );
    } finally {
      setIsDocumentLoading(false);
    }
  };

  const navigateToPrevDocument = async () => {
    if (!selectedDocument || messages.length === 0) return;

    const currentMessage = messages
      .slice()
      .reverse()
      .find((msg) => msg.role === "assistant" && msg.sources?.length);

    if (currentMessage?.sources) {
      const currentIndex = currentMessage.sources.findIndex(
        (source) => source.id === selectedDocument.id
      );

      if (currentIndex > 0) {
        const prevDocument = currentMessage.sources[currentIndex - 1];
        await handleDocumentClick(prevDocument);
      }
    }
  };

  const navigateToNextDocument = async () => {
    if (!selectedDocument || messages.length === 0) return;

    const currentMessage = messages
      .slice()
      .reverse()
      .find((msg) => msg.role === "assistant" && msg.sources?.length);

    if (currentMessage?.sources) {
      const currentIndex = currentMessage.sources.findIndex(
        (source) => source.id === selectedDocument.id
      );

      if (currentIndex < currentMessage.sources.length - 1) {
        const nextDocument = currentMessage.sources[currentIndex + 1];
        await handleDocumentClick(nextDocument);
      }
    }
  };

  const closeDocumentView = () => {
    setSelectedDocument(null);
  };

  // Handle keyboard navigation for document preview
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedDocument) return;

      if (e.key === "ArrowLeft") {
        navigateToPrevDocument();
      } else if (e.key === "ArrowRight") {
        navigateToNextDocument();
      } else if (e.key === "Escape") {
        closeDocumentView();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedDocument, messages]);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-b from-[#744796] to-[#48b2cd]">
      <AuraBackground />
      <SearchHeader />

      {/* Conversation History Sidebar */}
      {showConversationHistory && (
        <ConversationHistorySidebar
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          onNewConversation={clearSession}
        />
      )}

      <div className={`relative w-full max-w-7xl px-4 transition-all duration-300 ${
        isHistoryOpen ? 'ml-80' : ''
      }`}>
        <div className="flex flex-col items-center text-center">
          <img
            src={intelligentiaLogo}
            alt="Intelligentia Logo"
            className="w-80 object-contain"
          />

          <p className="mb-8 text-lg text-white/90">
            Accéder à l'intelligence humaine propulsée par l'IA
          </p>

          <div className="relative flex w-full justify-center">
            <div className="w-full max-w-6xl">
              {/* No Subscription Banner */}
              {!hasSubscription && (
                <NoSubscriptionScreen />
              )}

              {/* Access Denied Banner */}
              {hasSubscription && accessDenied && (
                <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                  <div className="flex items-center gap-3 text-red-200">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">Access Denied</p>
                      <p className="text-sm text-red-300 mt-1">{error || "You don't have permission to access this conversation."}</p>
                    </div>
                  </div>
                </div>
              )}

              {hasSubscription && <>
              {/* Chat Messages */}
              <div className="mb-4 max-h-[50vh] overflow-y-auto p-4 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:max-h-20 hover:[&::-webkit-scrollbar-thumb]:bg-white/20">
                {/* Show loading state for conversation history */}
                {isLoading && messages.length === 0 && conversationId && (
                  <div className="flex flex-col items-center justify-center py-8 text-white/70">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/30 mb-3"></div>
                    <p className="text-sm">Loading conversation history...</p>
                  </div>
                )}

                {/* Show empty state for existing conversation with no messages */}
                {conversationId && messages.length === 0 && !isLoading && (
                  <div className="mb-4 p-4 bg-white/5 rounded-lg border border-white/10 text-center">
                    <div className="text-white/60 text-sm">
                      <Brain className="w-5 h-5 mx-auto mb-2 opacity-50" />
                      {accessDenied ? (
                        <>
                          <p className="text-red-400 font-medium">Access Denied</p>
                          <p className="text-xs mt-1 opacity-75">
                            You don't have permission to access this conversation.
                          </p>
                          <p className="text-xs mt-1 opacity-75">
                            This conversation belongs to another user.
                          </p>
                        </>
                      ) : (
                        <>
                          <p>This conversation exists but has no messages yet.</p>
                          <p className="text-xs mt-1 opacity-75">
                            Start chatting to continue the conversation.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Render messages */}
                {messages.map((message, index) => (
                  <div key={index} className="mb-4">
                    <ChatMessageComponent
                      message={message}
                      onDocumentClick={handleDocumentClick}
                    />
                    {/* Show intent classification for debugging */}
                    {message.classification &&
                      process.env.NODE_ENV === "development" && (
                        <div className="mt-2 text-xs text-white/50 bg-black/20 rounded p-2">
                          Intent: {message.classification.intent}
                          (confidence:{" "}
                          {(message.classification.confidence * 100).toFixed(1)}
                          %)
                          {message.classification.reasoning && (
                            <div>
                              Reasoning: {message.classification.reasoning}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Usage Limit Banner */}
              <UsageLimitBanner />

              {/* Chat Input */}
              <div className="relative">
                <SearchInput
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onSearch={(value) => {
                    if (value.trim()) {
                      handleSearch(value);
                    }
                  }}
                  placeholder={accessDenied ? "Access denied - Cannot send messages" : "Posez votre question ou discutez avec moi..."}
                  disabled={isLoading || checkMessageLimit() || accessDenied || usage?.remaining === 0}
                  actions={
                    <>
                      <button
                        type="button"
                        onClick={clearSession}
                        className="px-3 py-1.5 text-sm text-white/70 hover:text-white border border-white/30 rounded-full transition-colors hover:bg-white/10 disabled:opacity-50"
                        disabled={isLoading}
                      >
                        ✕ Réinitialiser
                      </button>
                      {showConversationHistory && (
                        <button
                          type="button"
                          onClick={() => setIsHistoryOpen(true)}
                          className="px-3 py-1.5 text-sm text-white/70 hover:text-white border border-white/30 rounded-full transition-colors hover:bg-white/10 flex items-center gap-1.5 disabled:opacity-50"
                          disabled={isLoading}
                        >
                          <History className="w-3.5 h-3.5" />
                          Historique
                        </button>
                      )}
                      <div className="flex-1" />
                      <RagConfigToggle />
                    </>
                  }
                />

                {/* AI Disclaimer */}
                <p className="mt-4 text-sm text-white/60 text-center">
                  Le contenu généré par l'intelligence artificielle peut être inexact, incomplet ou ne pas refléter exactement les réalités juridiques, organisationnelles ou opérationnelles de votre milieu.
                </p>

                {/* Usage warning at 75%+ */}
                {usage && usage.remaining > 0 && usage.remaining <= Math.ceil(usage.limit * 0.25) && (
                  <div className="mt-2 flex items-center justify-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-yellow-300/80" />
                    <p className="text-sm text-yellow-300/80">
                      {usage.remaining} {usage.metering_mode === "chats" ? "conversation" : "message"}{usage.remaining > 1 ? "s" : ""} restant{usage.remaining > 1 ? "s" : ""} sur votre forfait — réinitialisation le {new Date(usage.period_end).toLocaleDateString("fr-CA", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                  </div>
                )}

                {/* Message limit indicator */}
                {(() => {
                  const actualMessages = messages.filter(
                    (msg) => !msg.isConversationNotice
                  );
                  const remainingMessages = 10 - actualMessages.length;

                  // Show indicator when approaching or exceeding the limit (8+ messages or already over 10)
                  if (actualMessages.length >= 8 || remainingMessages <= 0) {
                    return (
                      <div className="mt-2 text-center">
                        <div className="text-sm text-white/70">
                          {remainingMessages <= 0
                            ? "Limite de conversation atteinte - Nouvelle conversation requise pour assurer de meilleures réponses avec un contexte limité"
                            : `${remainingMessages} message${
                                remainingMessages !== 1 ? "s" : ""
                              } restant${
                                remainingMessages !== 1 ? "s" : ""
                              } avant limite`}
                        </div>
                        {remainingMessages <= 2 && remainingMessages > 0 && (
                          <div className="text-xs text-white/50 mt-1">
                            Les nouvelles conversations offrent de meilleures
                            réponses avec un contexte limité
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {isLoading && (
                <div className="mt-4 text-white/90">
                  <div className="flex items-center gap-4">
                    <div className="animate-pulse">
                      Réflexion - {formatThinkingTime(thinkingTime)} secondes
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-xl bg-red-500/10 p-4 text-white">
                  {error}
                </div>
              )}

              </>}

              {/* Document Preview - Keep the same as before */}
              {selectedDocument && (
                <div
                  className="mt-4 w-full rounded-xl bg-white/95 p-6 backdrop-blur-sm shadow-md animate-slideDown origin-top relative"
                  style={{ minHeight: "400px" }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-l font-bold text-black max-w-[85%] overflow-hidden">
                      {selectedDocument.title || "Untitled Document"}
                    </h2>
                    <button
                      className="text-black/70 hover:text-black/90 w-8 h-8 flex items-center justify-center transition-colors flex-shrink-0"
                      onClick={closeDocumentView}
                      aria-label="Close document preview"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="absolute top-3 right-12 text-[10px] text-black/50 px-2 py-1 rounded-md bg-gray-100">
                    <div className="flex flex-col">
                      <div className="flex items-center mb-1">
                        <span>← →: Navigate documents</span>
                      </div>
                      <div className="flex items-center">
                        <span>ESC: Close preview</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-sm text-black/70 mb-4 border-b border-black/10 pb-4">
                    <div>Document ID: {selectedDocument.id}</div>
                    {selectedDocument.score !== undefined && (
                      <div className="font-semibold bg-gray-100 rounded-lg px-3 py-1 text-black/90 flex items-center">
                        <span className="mr-1">Relevance:</span>
                        <span className="font-bold text-black">
                          {(selectedDocument.score * 100).toFixed(1)}%
                        </span>
                        <div className="ml-2 h-2 w-16 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-600"
                            style={{
                              width: `${Math.min(
                                100,
                                Math.round(selectedDocument.score * 100)
                              )}%`,
                              opacity: Math.max(0.5, selectedDocument.score),
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="prose max-w-none whitespace-pre-wrap text-left text-black overflow-auto">
                    {selectedDocument.content === "Loading content..." ? (
                      <div className="flex flex-col items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
                        <p className="text-black/70">
                          Loading document content...
                        </p>
                      </div>
                    ) : selectedDocument.content ===
                      "Error loading content. Please try again." ? (
                      <div className="flex flex-col items-center justify-center h-full">
                        <div className="bg-red-100 p-4 rounded-lg text-red-800 mb-4">
                          <p className="font-bold">Error loading content</p>
                          <p className="text-sm">
                            There was a problem retrieving the document content.
                          </p>
                          <button
                            className="mt-2 px-3 py-1 bg-red-700 text-white rounded-md text-sm hover:bg-red-800"
                            onClick={() =>
                              handleDocumentClick(selectedDocument)
                            }
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    ) : selectedDocument.formatted_content ? (
                      <ReactQuill
                        ref={quillRef}
                        value={selectedDocument.formatted_content}
                        readOnly
                        theme="bubble"
                        modules={{ toolbar: false }}
                      />
                    ) : (
                      selectedDocument.content || "No content available"
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
